import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import type { ClampQcInventoryBoxSnapshot } from "./clampQcInventoryBoxApi";
import { ClampQcInventoryBoxActions } from "./ClampQcInventoryBoxPrinterBar";
import { resolveClampQcPostQcDisplay } from "./clampQcCartonProgressDisplay";
import { CLAMP_QC_SERIAL_ANCHOR_RANGE } from "./clampQcSerialAnchor";

function ReadOnlyCartonCount({
  value,
  suffix,
  ariaLabel,
}: {
  value: number;
  suffix: string;
  ariaLabel: string;
}): JSX.Element {
  return (
    <div
      aria-label={ariaLabel}
      className="pointer-events-none flex items-center gap-px rounded-md bg-white/90 px-2 py-0.5 shadow-sm ring-1 ring-black/10 tabular-nums text-sm font-semibold text-slate-900 sm:gap-0.5 sm:px-2.5 sm:py-0.5"
    >
      <span>{String(value)}</span>
      <span className="shrink-0 select-none">/</span>
      <span className="shrink-0 select-none">{suffix}</span>
    </div>
  );
}

function IdlePostQcCartonCount({ ariaLabel }: { ariaLabel: string }): JSX.Element {
  return (
    <div
      aria-label={ariaLabel}
      className="pointer-events-none flex items-center gap-px rounded-md bg-white/90 px-2 py-0.5 shadow-sm ring-1 ring-black/10 tabular-nums text-sm font-semibold text-slate-500 sm:gap-0.5 sm:px-2.5 sm:py-0.5"
    >
      <span>--</span>
      <span className="shrink-0 select-none">/</span>
      <span className="shrink-0 select-none">--</span>
    </div>
  );
}

export function ClampQcCartonTrackersCard({
  boxCapacity,
  preQcRemaining,
  preQcDraft,
  serialAnchor,
  inventoryBox,
  onPreQcFocus,
  onPreQcBlur,
  onPreQcDraftChange,
  onStartNextPreQcCarton,
  inventoryBoxFlowEnabled,
  inventoryBoxSessionStarted,
  selectedPrinter,
  inventoryBoxBusy,
  onOpenAndPrintBox,
  onReprintBoxLabel,
  onSealBox,
  onStartNextBox,
}: {
  boxCapacity: number;
  preQcRemaining: number;
  preQcDraft: string;
  serialAnchor: number | null;
  inventoryBox: ClampQcInventoryBoxSnapshot | null;
  onPreQcFocus: () => void;
  onPreQcBlur: () => void;
  onPreQcDraftChange: (next: string) => void;
  onStartNextPreQcCarton: () => void;
  inventoryBoxFlowEnabled: boolean;
  inventoryBoxSessionStarted: boolean;
  selectedPrinter: string;
  inventoryBoxBusy: boolean;
  onOpenAndPrintBox: () => void;
  onReprintBoxLabel: () => void;
  onSealBox: () => void;
  onStartNextBox: () => void;
}): JSX.Element {
  const preQcEmpty = preQcRemaining <= 0;
  const preQcBarPct = Math.min(100, Math.round((Math.max(0, preQcRemaining) / boxCapacity) * 100));

  const postQcDisplay = resolveClampQcPostQcDisplay(inventoryBox, boxCapacity);
  const postQcFilled = postQcDisplay.kind === "open" ? postQcDisplay.filled : 0;
  const postQcCapacity = postQcDisplay.kind === "open" ? postQcDisplay.capacity : boxCapacity;
  const hasOpenBox = postQcDisplay.kind === "open";
  const postQcComplete = hasOpenBox && postQcFilled >= postQcCapacity;
  const postQcBarPct =
    postQcDisplay.kind === "open"
      ? Math.min(100, Math.round((Math.max(0, postQcFilled) / postQcCapacity) * 100))
      : 0;

  return (
    <Card className={cn(postQcComplete && hasOpenBox && "border-emerald-300 bg-emerald-50/90 shadow-sm")}>
      <CardHeader className="pb-2">
        <CardTitleWithHelp
          hoverHint={`Pre-QC counts down on every new inspection. Post-QC inventory box progress is tied to the scannable carton label: print or scan a box before recording passes. Passed units attach to that box until you seal it. Rescanning a serial adjusts post-QC when pass/fail changes; pre-QC does not change on rescan. The fill bar is read-only. At capacity, seal the box and open the next one. Scan the box Data Matrix to resume your open box.`}
        >
          Carton progress
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-4 text-sm pt-2">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pre-QC carton</p>
          <div className="relative h-9 overflow-hidden rounded-full bg-slate-100 ring-1 ring-black/5">
            <div
              className={cn(
                "absolute inset-y-0 left-0 z-[1] rounded-full transition-[width] duration-300",
                preQcEmpty ? "bg-amber-500" : "bg-brand-600",
              )}
              style={{ width: `${preQcBarPct}%` }}
            />
            <div className="pointer-events-none absolute inset-0 z-[2] flex items-center justify-center" aria-live="polite">
              <label
                htmlFor="clamp-pre-qc-carton-count"
                className="pointer-events-auto flex cursor-text items-center gap-px rounded-md bg-white/90 px-2 py-0.5 shadow-sm ring-1 ring-black/10 tabular-nums text-sm font-semibold text-slate-900 sm:gap-0.5 sm:px-2.5 sm:py-0.5"
              >
                <input
                  id="clamp-pre-qc-carton-count"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  aria-label={`Units remaining in pre-QC carton (${String(boxCapacity)} per carton)`}
                  style={{
                    width: `${Math.min(10, Math.max(1, preQcDraft.replace(/\D/g, "").length || 1)) + 2}ch`,
                  }}
                  className="min-w-[2ch] max-w-[12ch] border-0 bg-transparent p-0 text-center outline-none ring-0 focus:ring-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-500"
                  value={preQcDraft}
                  onFocus={onPreQcFocus}
                  onBlur={onPreQcBlur}
                  onChange={(event) => {
                    onPreQcDraftChange(event.target.value.replace(/[^\d]/g, ""));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      (event.currentTarget as HTMLInputElement).blur();
                    }
                  }}
                />
                <span className="pointer-events-none shrink-0 select-none">/</span>
                <span className="pointer-events-none shrink-0 select-none">{String(boxCapacity)}</span>
              </label>
            </div>
          </div>
          {serialAnchor != null ? (
            <p className="text-xs text-slate-600">
              Serial window: <span className="font-mono font-semibold text-slate-800">{String(serialAnchor)}</span>
              {" +/- "}
              {String(CLAMP_QC_SERIAL_ANCHOR_RANGE)}
            </p>
          ) : (
            <p className="text-xs text-slate-500">First scan on this carton sets the serial window.</p>
          )}
          {preQcEmpty ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button type="button" onClick={onStartNextPreQcCarton}>
                Start next pre-QC carton
              </Button>
            </div>
          ) : null}
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Post-QC inventory box</p>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              {postQcDisplay.kind === "open" ? (
                <p className="text-xs text-slate-600">
                  Carton <span className="font-mono font-semibold">{postQcDisplay.shortCode}</span>
                  <span className="ml-2 rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-800">
                    Open
                  </span>
                </p>
              ) : (
                <p className="text-xs text-slate-500">{postQcDisplay.label}</p>
              )}
            </div>
            {inventoryBoxFlowEnabled ? (
              <ClampQcInventoryBoxActions
                boxCapacity={boxCapacity}
                selectedPrinter={selectedPrinter}
                inventoryBox={inventoryBox}
                inventoryBoxSessionStarted={inventoryBoxSessionStarted}
                inventoryBoxBusy={inventoryBoxBusy}
                onOpenAndPrintBox={onOpenAndPrintBox}
                onReprintBoxLabel={onReprintBoxLabel}
                onSealBox={onSealBox}
                onStartNextBox={onStartNextBox}
              />
            ) : null}
          </div>
          <div className="relative h-9 overflow-hidden rounded-full bg-slate-100 ring-1 ring-black/5">
            <div
              className={cn(
                "absolute inset-y-0 left-0 z-[1] rounded-full transition-[width] duration-300",
                postQcComplete && hasOpenBox ? "bg-emerald-600" : "bg-brand-600",
              )}
              style={{ width: `${postQcBarPct}%` }}
            />
            <div className="pointer-events-none absolute inset-0 z-[2] flex items-center justify-center" aria-live="polite">
              {postQcDisplay.kind === "open" ? (
                <ReadOnlyCartonCount
                  value={postQcFilled}
                  suffix={String(postQcCapacity)}
                  ariaLabel={`Passed units in post-QC box (${String(postQcCapacity)} per box)`}
                />
              ) : (
                <IdlePostQcCartonCount ariaLabel="No post-QC carton open" />
              )}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
